import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowUpRight, LogIn, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { faqData } from "../../data/pricing";
import { useClerk, useAuth } from "@clerk/clerk-react";
import UpgradeModal from "../../components/billing/UpgradeModal";

// Post-login landing route (see App.js "/" route).
const LANDING_ROUTE = "/";

const JoinPricing = () => {
  const { openSignIn, redirectToSignUp } = useClerk();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Plans are defined here so their CTAs can close over Clerk + modal state.
  const plans = [
    {
      name: "14-Day Free Trial",
      tagline: "Try everything",
      price: "Free",
      priceNote: "14 days",
      features: [
        "Full access to all 15 maps",
        "Continuity planning & nominee sharing",
        "No credit card required",
        "Export not included",
      ],
      cta: "Create account",
      note: "Starts automatically when you sign up.",
      highlighted: false,
      isPaid: false,
      onClick: () =>
        isSignedIn
          ? navigate(LANDING_ROUTE)
          : redirectToSignUp({
              signUpForceRedirectUrl: `${window.location.origin}${LANDING_ROUTE}`,
            }),
    },
    {
      name: "Standard",
      tagline: "Map & maintain",
      price: "$5",
      priceNote: "/month · or $33/year",
      features: [
        "Everything in the trial",
        "PDF & CSV export",
        "All 15 asset maps",
        "Continuity planning & nominee sharing",
        "Save 45% with annual billing",
      ],
      cta: "Get started",
      note: "No passwords, seed phrases, or private keys stored.",
      highlighted: true,
      isPaid: true,
      // Signed-in users upgrade in place; new visitors are sent to sign-up,
      // then straight to Settings where ?upgrade=standard auto-opens this
      // same modal so their intent to buy carries through sign-up.
      onClick: () =>
        isSignedIn
          ? setShowUpgrade(true)
          : redirectToSignUp({
              signUpForceRedirectUrl: `${window.location.origin}/settings?upgrade=standard`,
            }),
    },
    {
      name: "Plus",
      tagline: "Reference & store",
      price: "Coming soon",
      priceNote: "",
      features: [
        "Everything in Standard",
        "SMS continuity check-ins",
        "Non-sensitive reference document storage",
        "And much more",
      ],
      cta: "Coming Soon",
      note: "Not for sensitive documents or credentials.",
      highlighted: false,
      comingSoon: true,
      isPaid: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="join-pricing-page">
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a
            href="https://lifely.guide"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to main site
          </a>
          <div className="font-serif text-xl text-foreground">Lifely</div>
          <button
            onClick={() =>
              openSignIn({
                forceRedirectUrl: `${window.location.origin}${LANDING_ROUTE}`,
              })
            }
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogIn className="w-4 h-4" />
            Sign in
          </button>
        </div>
      </header>

      {/* Header */}
      <section className="pt-32 pb-14 md:pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4 animate-fade-in">
            Plans
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground animate-fade-in">
            Pricing
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl animate-fade-in">
            Start with a free 14-day trial. Upgrade to Standard anytime, no credit card needed to begin.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <div
                key={i}
                className="flex animate-fade-in"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <Card
                  className={`flex-1 ${
                    plan.highlighted
                      ? "border-primary/25 shadow-lg ring-1 ring-primary/10"
                      : "border-border/60"
                  } bg-card hover:shadow-md transition-shadow duration-300`}
                >
                  <CardContent className="p-8 flex flex-col h-full">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {plan.name}
                        </h3>
                        {plan.highlighted && (
                          <span className="text-[0.65rem] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {plan.tagline}
                      </p>
                      <div className="mt-8 mb-8">
                        <span className="text-4xl font-serif text-foreground">
                          {plan.price}
                        </span>
                        {plan.priceNote && (
                          <span className="text-sm text-muted-foreground ml-1.5">
                            {plan.priceNote}
                          </span>
                        )}
                      </div>
                      <ul className="space-y-3.5 mb-8">
                        {plan.features.map((feature, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <Check className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                            <span className="text-sm text-foreground/80">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-auto">
                      {plan.comingSoon ? (
                        <button
                          disabled
                          className="w-full rounded-full h-11 opacity-50 cursor-not-allowed border border-input bg-background px-4 py-2 text-sm font-medium"
                        >
                          {plan.cta}
                        </button>
                      ) : plan.isPaid ? (
                        <button
                          onClick={plan.onClick}
                          className="w-full rounded-full h-11 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors bg-foreground text-background hover:bg-foreground/85"
                          data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                        >
                          {plan.cta}
                          <ArrowUpRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={plan.onClick}
                          className="w-full rounded-full h-11 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                          data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
                        >
                          {plan.cta}
                          <ArrowUpRight className="w-4 h-4" />
                        </button>
                      )}
                      {plan.note && (
                        <p className="text-xs text-muted-foreground mt-3 text-center">
                          {plan.note}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-secondary/40">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl text-foreground text-center mb-12">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-card border border-border/50 rounded-xl px-6 data-[state=open]:shadow-sm transition-shadow"
              >
                <AccordionTrigger className="text-left text-foreground font-medium py-5 hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Lifely. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Upgrade modal for already-signed-in visitors choosing Standard */}
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="upgrade_prompt" />
    </div>
  );
};

export default JoinPricing;
